import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTableFilters } from '@/components/data-table/filters-v2';
import { Separator } from '@/components/ui/separator';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { PageTemplate } from '@/modules/common/components/templates/page-template';
import { AuthenticatedRoutes } from '@/router/routes';

import { TimeOffApprovalsTable } from '../components/approvals-table/time-off-approvals-table';
import { TimeOffApprovalsToolbar } from '../components/approvals-toolbar/time-off-approvals-toolbar';
import {
  FILTER_KEY_DATE_RANGE,
  FILTER_KEY_TIME_CATEGORY,
  TIME_OFF_APPROVALS_TABLE_FILTER_KEYS,
  TIME_OFF_APPROVALS_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/time-off-approvals-filter-keys';
import { getTimeOffsFiltersConfig } from '../constants/time-offs-filters-config';
import { useTimeOffApprovalsTable } from '../hooks/use-time-off-approvals-table';
import { useTimeOffsDateRangeParam } from '../hooks/use-time-offs-date-range-param';
import { TimeOffRequest } from '../types/time-off-request';
import { TimeOffTimeCategory } from '../types/time-off-time-category';

export const TimeOffApprovalsPage = () => {
  const { t } = useTranslation('authenticated.timeOff');
  const navigate = useNavigate();

  // Open the request in the details page (approve mode). Pass the full request
  // via router state so the review modal has it without an extra fetch; the
  // details page falls back to fetching by id on hard refresh.
  const buildRowHref = (request: TimeOffRequest) =>
    AuthenticatedRoutes.TimeOffApprovalDetails.replace(':requestId', String(request.id));

  const handleRowClick = (request: TimeOffRequest) =>
    navigate(buildRowHref(request), {
      state: { request },
    });

  usePersistedTableFilters({
    storageKey: TIME_OFF_APPROVALS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: TIME_OFF_APPROVALS_TABLE_FILTER_KEYS,
    defaultFilters: { [FILTER_KEY_TIME_CATEGORY]: TimeOffTimeCategory.Upcoming },
  });

  // Same filter set as the Time-Offs page (Type, Status, Time category) — all
  // backed by the to-review endpoint's filter shape.
  const filtersConfig = useMemo(() => getTimeOffsFiltersConfig(), []);
  const filterState = useTableFilters(filtersConfig);
  const dateRange = useTimeOffsDateRangeParam();

  // The date-range lives outside the filters-v2 config, so it's absent from
  // `activeFilters`. Merge it back in so it reaches both the query key (triggers
  // refetch) and `mapTimeOffFilters` (applies the filter).
  const tableFilters = useMemo(
    () =>
      dateRange.serialized
        ? { ...filterState.activeFilters, [FILTER_KEY_DATE_RANGE]: dateRange.serialized }
        : filterState.activeFilters,
    [filterState.activeFilters, dateRange.serialized]
  );

  const { table, isLoading, hasMore, loadMore, errorMessage, saveColumnSizing } =
    useTimeOffApprovalsTable(tableFilters, t);

  return (
    <PageTemplate
      title={t('approvals.title')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />

      {/* Single page scroll container. Toolbar pins at top; table scrolls horizontally. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pt-4">
        <TimeOffApprovalsToolbar
          filtersConfig={filtersConfig}
          filterState={filterState}
          dates={dateRange.dates}
          selectedPreset={dateRange.selectedPreset}
          onDateChange={dateRange.onChange}
          onPresetSelect={dateRange.onPresetSelect}
          onDateReset={dateRange.onReset}
          table={table}
        />

        <TimeOffApprovalsTable
          table={table}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          error={errorMessage}
          onResizeEnd={saveColumnSizing}
          hasActiveFilters={filterState.hasActiveFilters}
          onRowClick={handleRowClick}
          getRowHref={buildRowHref}
        />
      </div>
    </PageTemplate>
  );
};
